import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    FaSearch, 
    FaCopy, 
    FaFileExcel, 
    FaPrint, 
    FaFilePdf,
    FaArrowUp,
    FaArrowDown,
    FaTimes
} from 'react-icons/fa';

const ZpHeadOpenTransferWorkList = () => {
    const [works, setWorks] = useState([]);
    const [lotteryResults, setLotteryResults] = useState({});
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal State
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [selectedWorkForDoc, setSelectedWorkForDoc] = useState(null);
    const [additionalDocsText, setAdditionalDocsText] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const resultsRef = ref(db, 'ekamwatapzp/LotteryResults');
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');

        onValue(resultsRef, (resultsSnapshot) => {
            const resultsData = resultsSnapshot.val() || {};
            setLotteryResults(resultsData);
            
            onValue(worksRef, (worksSnapshot) => {
                const worksData = worksSnapshot.val() || {};
                let filteredWorks = [];
                let datesSet = new Set();

                Object.entries(worksData).forEach(([meetingNum, meetingWorks]) => {
                    if (typeof meetingWorks === 'object' && meetingWorks !== null) {
                        Object.entries(meetingWorks).forEach(([id, work]) => {
                            const meetingPath = meetingNum.toString().replace(/[.#$[\]]/g, "_");
                            const workPath = (work.workNumber || '').toString().replace(/[.#$[\]]/g, "_");

                            const winnerId = resultsData[meetingPath]?.[workPath];
                            
                            const isEngineer = winnerId && typeof winnerId === 'string' && winnerId.startsWith('Engineer_');
                            const isSociety = winnerId && typeof winnerId === 'string' && winnerId.startsWith('Society_');

                            if (winnerId && !isEngineer && !isSociety) {
                                filteredWorks.push({
                                    id,
                                    meetingNum,
                                    ...work,
                                    winnerId,
                                    type: "Civil"
                                });

                                if (work.allocatedAt) {
                                    const dateStr = new Date(work.allocatedAt).toISOString().split('T')[0];
                                    datesSet.add(dateStr);
                                }
                            }
                        });
                    }
                });

                setWorks(filteredWorks.reverse());
                setAvailableDates(Array.from(datesSet).sort().reverse());
                setLoading(false);
            });
        });

        return () => unsubscribe();
    }, [navigate]);

    const handleOpenDocModal = (work) => {
        setSelectedWorkForDoc(work);
        setAdditionalDocsText(work.additionalDocumentsText || '');
        setIsDocModalOpen(true);
    };

    const handleSaveAdditionalDocs = async () => {
        if (!selectedWorkForDoc) return;
        
        setIsSaving(true);
        try {
            const workRef = ref(db, `ekamwatapzp/WorkNames/${selectedWorkForDoc.meetingNum}/${selectedWorkForDoc.id}`);
            await update(workRef, {
                additionalDocumentsText: additionalDocsText
            });
            toast.success("माहिती जतन केली!");
            setIsDocModalOpen(false);
        } catch (error) {
            toast.error("त्रुटी: " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const filteredData = works.filter(item => {
        const searchStr = searchTerm.toLowerCase();
        const matchesSearch = 
            (item.workNumber || '').toLowerCase().includes(searchStr) ||
            (item.workName || '').toLowerCase().includes(searchStr) ||
            (item.taluka || '').toLowerCase().includes(searchStr) ||
            (item.schemeName || '').toLowerCase().includes(searchStr);

        const matchesDate = !selectedDate || 
            (item.allocatedAt && item.allocatedAt.startsWith(selectedDate));

        return matchesSearch && matchesDate;
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans text-gray-800">
            {/* Breadcrumb / Top Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">
                    ओपन (खुला) कामाची यादी
                </h1>
                <div className="text-[11px] md:text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400 font-bold">ट्रान्स्फर वर्क यादी</span>
                </div>
            </div>

            {/* Lottery Date Filter Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-6 font-sans">
                <div className="flex flex-col gap-3">
                    <label className="text-sm font-bold text-gray-700">
                        लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 max-w-2xl">
                        <select 
                            className="bg-white border border-gray-200 rounded-md py-2 px-4 focus:ring-2 focus:ring-blue-500/20 outline-none transition font-medium text-sm"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        >
                            <option value="">लॉटरीची तारीख निवडा</option>
                            {availableDates.map(date => (
                                <option key={date} value={date}>{date}</option>
                            ))}
                        </select>
                        <button className="bg-[#4e73df] hover:bg-blue-700 text-white px-8 py-2 rounded-md font-bold shadow-sm transition active:scale-95 text-sm">
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom Styled Table Container */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden font-sans">
                
                {/* Toolbar Section */}
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-1.5 self-start">
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Copy</button>
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Excel</button>
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Print</button>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <span className="text-sm font-bold text-gray-500">Search:</span>
                        <input 
                            type="text" 
                            className="flex-grow md:w-64 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500/20 outline-none transition bg-white"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Data Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-y border-gray-200">
                            <tr>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center w-16">
                                    # <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    वर्क आयडी <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    टाईप <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    तालुका <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    निविदा रक्कम <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">
                                    कामाचे नाव <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest text-center">
                                    अतिरिक्त कागदपत्रे <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 italic">
                            {filteredData.map((item, index) => (
                                <tr 
                                    key={item.id} 
                                    className={`transition-all font-medium text-gray-700 ${index === 0 ? 'bg-[#dcfce7] hover:bg-[#d1fae5]' : 'hover:bg-blue-50/20'}`}
                                >
                                    <td className="px-5 py-4 border-r text-center text-sm font-bold">{index + 1}</td>
                                    <td className="px-5 py-4 border-r text-center text-sm font-bold">{item.workNumber || item.id.substring(0, 5)}</td>
                                    <td className="px-5 py-4 border-r text-center text-sm font-bold">{item.type}</td>
                                    <td className="px-5 py-4 border-r text-center text-sm font-bold">{item.taluka}</td>
                                    <td className="px-5 py-4 border-r text-center text-sm font-black">{item.tenderAmount}</td>
                                    <td className="px-5 py-4 border-r text-sm leading-relaxed">
                                        <div className="flex flex-col">
                                            <span className="font-bold">{item.workName}</span>
                                            <span className="text-[11px] font-black text-gray-600 mt-1 italic">अतिरिक्त कागदपत्रे : {item.additionalDocumentsText || '- .'}</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-center">
                                        <div className="flex justify-center">
                                            <FaFilePdf 
                                                className="text-gray-400 hover:text-red-500 cursor-pointer transition text-lg" 
                                                onClick={() => handleOpenDocModal(item)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {filteredData.length === 0 && (
                        <div className="py-12 text-center text-gray-400 font-bold bg-[#f8f9fa] border-b border-gray-100">
                            No data available in table
                        </div>
                    )}
                </div>

                {/* Footer Section */}
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                    <div className="text-sm font-medium text-gray-500">
                        Showing {filteredData.length === 0 ? "0 to 0 of 0" : `1 to ${filteredData.length} of ${filteredData.length}`} entries
                    </div>
                    <div className="flex border border-gray-200 rounded overflow-hidden">
                        <button className="px-4 py-2 text-sm font-black text-gray-400 bg-gray-50/50 hover:bg-gray-100 disabled:opacity-50 border-r border-gray-200" disabled>Previous</button>
                        <button className="px-4 py-2 text-sm font-black text-gray-400 bg-gray-50/50 hover:bg-gray-100 disabled:opacity-50" disabled>Next</button>
                    </div>
                </div>
            </div>

            {/* Additional Documents Modal */}
            {isDocModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 font-sans">
                    <div className="bg-white w-full max-w-xl rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="text-[17px] font-bold text-gray-800">
                                कामाचे नाव - {selectedWorkForDoc?.workName}
                            </h2>
                            <button 
                                onClick={() => setIsDocModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 transition"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            <label className="block text-sm font-bold text-gray-600 mb-3">
                                अतिरिक्त कागदपत्रे
                            </label>
                            <textarea
                                className="w-full h-32 p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 outline-none transition text-sm text-gray-700 resize-none font-medium"
                                placeholder="Enter document names here..."
                                value={additionalDocsText}
                                onChange={(e) => setAdditionalDocsText(e.target.value)}
                            />
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
                            <button 
                                onClick={() => setIsDocModalOpen(false)}
                                className="px-6 py-2 bg-[#6c757d] hover:bg-gray-700 text-white rounded font-bold text-sm transition"
                            >
                                Close
                            </button>
                            <button 
                                onClick={handleSaveAdditionalDocs}
                                disabled={isSaving}
                                className="px-6 py-2 bg-[#4e73df] hover:bg-blue-700 text-white rounded font-bold text-sm transition shadow-md disabled:opacity-50 flex items-center gap-2"
                            >
                                {isSaving ? 'Saving...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ZpHeadOpenTransferWorkList;
